/**
 * TanStack Query setup + typed helpers bound to the KFMS envelope.
 *
 * Query keys are `['api', '/api/v1/<path>', kind, params?]`, so invalidating a path prefix
 * (`invalidateApi('/core/items')`) refreshes the list AND every `/core/items/{id}` resource.
 *
 * @example
 *   const { data: items, meta, isLoading } = useList<Item>('/core/items', { page, q });
 *   const { data: item } = useResource<Item>(`/core/items/${id}`);
 *   const save = useApiMutation({
 *     mutationFn: (v: ItemForm) => api.post<Item>('/core/items', v),
 *     invalidate: ['/core/items'],
 *     successMessage: 'common:messages.saved',
 *   });
 */
import {
  QueryClient,
  keepPreviousData,
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import i18n from 'i18next';
import { ApiError, apiPath, get, list, type ListMeta, type ListResponse, type QueryParams } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        if (error instanceof ApiError && (error.isNetworkError || error.status >= 500)) return count < 2;
        return false;
      },
    },
    mutations: { retry: false },
  },
});

function cleanParams(params?: QueryParams): QueryParams | undefined {
  if (!params) return undefined;
  const out: QueryParams = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

export const apiKeys = {
  all: ['api'] as const,
  list: (path: string, params?: QueryParams) => ['api', apiPath(path), 'list', cleanParams(params) ?? null] as const,
  resource: (path: string, params?: QueryParams) => ['api', apiPath(path), 'one', cleanParams(params) ?? null] as const,
};

/** Invalidates every cached list/resource whose path starts with one of the prefixes. */
export function invalidateApi(...prefixes: string[]): Promise<void> {
  const normalised = prefixes.map(apiPath);
  return queryClient.invalidateQueries({
    predicate: (q) => {
      const p = q.queryKey[1];
      return q.queryKey[0] === 'api' && typeof p === 'string' && normalised.some((pre) => p === pre || p.startsWith(`${pre}/`) || p.startsWith(`${pre}?`));
    },
  });
}

export type ListQueryResult<T> = Omit<UseQueryResult<ListResponse<T>, ApiError>, 'data'> & {
  /** rows of the current page (empty array while loading) */
  data: T[];
  meta: ListMeta | undefined;
};

type ListOptions<T> = Omit<UseQueryOptions<ListResponse<T>, ApiError>, 'queryKey' | 'queryFn'>;

/**
 * Paged list query. Pass `null` as path to disable.
 * Keeps the previous page visible while the next page loads.
 */
export function useList<T>(resourcePath: string | null, params?: QueryParams, options?: ListOptions<T>): ListQueryResult<T> {
  const q = useQuery<ListResponse<T>, ApiError>({
    queryKey: apiKeys.list(resourcePath ?? '__disabled__', params),
    queryFn: ({ signal }) => list<T>(resourcePath as string, cleanParams(params), { signal }),
    enabled: resourcePath !== null && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    ...options,
  });
  return { ...q, data: q.data?.data ?? [], meta: q.data?.meta };
}

/**
 * Fetches every page of a list (pageSize 200, the contract maximum) — for small catalogs
 * such as permissions or UoMs. Stops at `max` rows.
 */
export async function listAll<T>(path: string, params?: QueryParams, max = 5000, signal?: AbortSignal): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; out.length < max; page++) {
    const res = await list<T>(path, { ...cleanParams(params), page, pageSize: 200 }, { signal });
    out.push(...res.data);
    if (res.data.length < 200 || out.length >= (res.meta?.total ?? 0)) break;
  }
  return out;
}

/** `useQuery` over {@link listAll}. */
export function useListAll<T>(path: string | null, params?: QueryParams, options?: Omit<UseQueryOptions<T[], ApiError>, 'queryKey' | 'queryFn'>): UseQueryResult<T[], ApiError> {
  return useQuery<T[], ApiError>({
    queryKey: ['api', apiPath(path ?? '__disabled__'), 'all', cleanParams(params) ?? null],
    queryFn: ({ signal }) => listAll<T>(path as string, params, 5000, signal),
    enabled: path !== null && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

type ResourceOptions<T> = Omit<UseQueryOptions<T, ApiError>, 'queryKey' | 'queryFn'>;

/** Single resource (`{ data }` envelope unwrapped). Pass `null` to disable. */
export function useResource<T>(path: string | null, params?: QueryParams, options?: ResourceOptions<T>): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>({
    queryKey: apiKeys.resource(path ?? '__disabled__', params),
    queryFn: ({ signal }) => get<T>(path as string, cleanParams(params), { signal }),
    enabled: path !== null && (options?.enabled ?? true),
    ...options,
  });
}

export interface ApiMutationOptions<TVars, TResult> extends Omit<UseMutationOptions<TResult, ApiError, TVars>, 'mutationFn'> {
  mutationFn: (vars: TVars) => Promise<TResult>;
  /** Path prefixes to invalidate on success (static or computed). */
  invalidate?: string[] | ((vars: TVars, result: TResult) => string[]);
  /** i18n key of a success toast (e.g. `common:messages.saved`). */
  successMessage?: string;
  /** Show a toast with the problem detail on error (default false — prefer `<ProblemAlert>` in forms). */
  toastError?: boolean;
}

/** `useMutation` with path-prefix invalidation and optional success/error toasts. */
export function useApiMutation<TVars = void, TResult = unknown>(
  options: ApiMutationOptions<TVars, TResult>,
): UseMutationResult<TResult, ApiError, TVars> {
  const { mutationFn, invalidate, successMessage, toastError, onSuccess, onError, ...rest } = options;
  return useMutation<TResult, ApiError, TVars>({
    ...rest,
    mutationFn: async (vars) => {
      try {
        return await mutationFn(vars);
      } catch (e) {
        if (e instanceof ApiError) throw e;
        throw new ApiError({ status: -1, code: 'CLIENT_ERROR', detail: e instanceof Error ? e.message : String(e) });
      }
    },
    onSuccess: async (...args) => {
      const [result, vars] = args;
      const prefixes = typeof invalidate === 'function' ? invalidate(vars, result) : invalidate;
      if (prefixes?.length) await invalidateApi(...prefixes);
      if (successMessage) notifications.show({ color: 'green', message: i18n.t(successMessage) });
      await onSuccess?.(...args);
    },
    onError: (...args) => {
      const [err] = args;
      if (toastError) notifications.show({ color: 'red', title: i18n.t('common:errors.title'), message: err.detail ?? err.message });
      onError?.(...args);
    },
  });
}
